<template>
  <tbody class="w-full">
    <template v-for="(item, idx) of groupedView.items" :key="`row${idx}`">
      <tr
        v-if="groupedView.groups?.has(idx)"
        class="h-10 bg-n-50 dark:bg-n-800"
      >
        <td :colspan="totalColumns" class="px-4 py-2">
          <template v-if="groupBy && groupSlotKey in groupSlots">
            <component
              :is="groupSlots[groupSlotKey]"
              v-bind="groupedView.groups.get(idx)"
            />
          </template>
          <span v-else class="text-sm text-n-500">
            {{ groupedView.groups.get(idx)?.value }}
          </span>
        </td>
      </tr>
      <tr
        class="h-14 text-n-800 dark:text-n-0 border-b border-b-n-50 dark:border-n-700"
        :class="[
          item.grouped ? 'bg-n-10 dark:bg-transparent' : 'bg-n-0 dark:bg-transparent',
          { 'hover:bg-n-10 cursor-pointer': hasClickableRows }
        ]"
        @click="onRowClicked(item.originalIndex)"
      >
        <td
          v-if="hasMultiSelect"
          @click.stop="
            () => {
              rowSelectionState[item.originalIndex] = !rowSelectionState[item.originalIndex]
              onCheckboxClick()
            }
          "
          class="px-2 h-full"
        >
          <l-checkbox
            v-model="rowSelectionState[item.originalIndex]"
            small
            @update:model-value="onCheckboxClick"
          />
        </td>
        <td
          v-for="column of columns"
          :key="`row${idx}-column-${column.key}`"
          class="w-full h-full [&_>_*]:min-h-14 overflow-hidden"
        >
          <slot :name="`cell(${column.key})`" :row="item.row" :column="column" :row-index="item.originalIndex">
            <template v-if="Object.keys(parentSlots).includes(`cell(${column.key})`)">
              <component
                :is="parentSlots[`cell(${column.key})`]"
                :column="column"
                :row="item.row"
                :row-index="item.originalIndex"
                @checkbox="onCellCheckboxClick"
                @radio="onCellRadioClick"
                @switch="onCellSwitchClick"
              />
            </template>
            <template v-else>
              <l-table-cell
                :column="column"
                :row="item.row"
                :row-index="item.originalIndex"
                @checkbox="onCellCheckboxClick"
                @radio="onCellRadioClick"
                @switch="onCellSwitchClick"
              />
            </template>
          </slot>
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

import LCheckbox from '../LCheckbox.vue'
import LTableCell from './LTableCell.vue'
import type { GroupSlotProps, ParentSlots, TableDataColumn } from './types'

type GroupedItem = {
  row: Record<string, any>
  originalIndex: number
  grouped: boolean
}

type GroupedView = {
  items: GroupedItem[]
  groups: Map<number, GroupSlotProps> | null
}

type Props = {
  columns: TableDataColumn[]
  data: Record<string, any>[]
  hasMultiSelect?: boolean
  parentSlots?: ParentSlots
  hasClickableRows?: boolean
  groupBy?: string
  groupSlots?: ParentSlots
}

const props = withDefaults(defineProps<Props>(), {
  hasMultiSelect: false,
  hasClickableRows: false,
  parentSlots: () => ({}),
  groupSlots: () => ({})
})

const emit = defineEmits<{
  rowClicked: [rowIndex: number]
  cellCheckboxChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  cellRadioChange: [value: boolean, rowIndex: number, column: TableDataColumn]
  cellSwitchChange: [value: boolean, rowIndex: number, column: TableDataColumn]
}>()

const selectedRows = defineModel<number[]>('selectedRows', { default: [] })
const rowSelectionState = ref<boolean[]>([])

watch(selectedRows, () => {
  const newSelection = new Array(props.data.length).fill(false)
  selectedRows.value.forEach(rowIndex => {
    newSelection[rowIndex] = true
  })
  rowSelectionState.value = newSelection
})

const selectedIndices = computed<number[]>(() => {
  return rowSelectionState.value.reduce((acc: number[], row: boolean, idx: number): number[] => {
    if (row) acc.push(idx)
    return acc
  }, [])
})

const groupSlotKey = computed(() => `group(${props.groupBy})`)

const totalColumns = computed(() => {
  return props.columns.length + (props.hasMultiSelect ? 1 : 0)
})

const groupedView = computed<GroupedView>(() => {
  if (!props.groupBy) {
    return {
      items: props.data.map((row, i) => ({ row, originalIndex: i, grouped: false })),
      groups: null
    }
  }

  const key = props.groupBy
  const buckets = new Map<any, { rows: Record<string, any>[]; indices: number[] }>()

  for (let i = 0; i < props.data.length; i++) {
    const row = props.data[i]
    const value = row[key]
    if (!buckets.has(value)) {
      buckets.set(value, { rows: [], indices: [] })
    }
    const bucket = buckets.get(value)!
    bucket.rows.push(row)
    bucket.indices.push(i)
  }

  const items: GroupedItem[] = []
  const groups = new Map<number, GroupSlotProps>()

  for (const [value, bucket] of buckets) {
    const isGroup = bucket.rows.length > 1
    if (isGroup) {
      groups.set(items.length, { value, rows: bucket.rows })
    }
    for (let j = 0; j < bucket.rows.length; j++) {
      items.push({ row: bucket.rows[j], originalIndex: bucket.indices[j], grouped: isGroup })
    }
  }

  return { items, groups }
})

onBeforeMount(() => {
  rowSelectionState.value = new Array(props.data.length).fill(false)
})

function onRowClicked(index: number): void {
  if (props.hasClickableRows) {
    emit('rowClicked', index)
  }
}

function onCheckboxClick(): void {
  selectedRows.value = selectedIndices.value
}

function onCellCheckboxClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellCheckboxChange', value, rowIndex, column)
}

function onCellRadioClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellRadioChange', value, rowIndex, column)
}

function onCellSwitchClick(value: boolean, rowIndex: number, column: TableDataColumn): void {
  emit('cellSwitchChange', value, rowIndex, column)
}
</script>
